import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { SUBSCRIPTION_STATUS_OPTIONS } from '../../../constants/subscriptionStatusOptions';
import type { SubscriptionStatus } from '../../../types/subscriptionStatus';
import type { FeatureFlagStoreSelectorProps } from '../../../types/features/featureFlags';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import {
  FEATURE_FLAG_SELECTOR_LIST_SX,
  FEATURE_FLAG_SELECTOR_STATE_SX,
} from '../constants/featureFlagSelectorStyles';

const FeatureFlagStoreSelector: React.FC<FeatureFlagStoreSelectorProps> = ({
  onBulkStoreAssignmentChange,
  onSearchChange,
  onStatusFilterChange,
  onStoreAssignmentChange,
  search,
  selectedStoreIds,
  statusFilter,
  storeIdsError,
  storesError,
  storesLoading,
  stores,
}) => {
  const visibleStoreIds = stores.map((store) => store.id);
  const selectedStoreIdSet = new Set(selectedStoreIds);
  const selectedVisibleStoreCount = visibleStoreIds.filter((storeId) => selectedStoreIdSet.has(storeId)).length;
  const hasVisibleStores = visibleStoreIds.length > 0;
  const bulkActionsDisabled = storesLoading || Boolean(storesError) || !hasVisibleStores;

  return (
    <Stack spacing={1.25}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          fullWidth
          label="Search Stores"
          size="small"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search by store name..."
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff', borderRadius: '8px' },
          }}
        />
        <FormControl
          fullWidth
          size="small"
          sx={{
            width: { xs: '100%', sm: 190 },
            '& .MuiSelect-select': { padding: '10px 12px !important' },
          }}
        >
          <InputLabel id="feature-flag-store-status-filter-label">Subscription Status</InputLabel>
          <Select
            labelId="feature-flag-store-status-filter-label"
            label="Subscription Status"
            value={statusFilter}
            onChange={(event: SelectChangeEvent<SubscriptionStatus>) =>
              onStatusFilterChange(event.target.value as SubscriptionStatus)
            }
          >
            {SUBSCRIPTION_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
          {selectedStoreIds.length} selected - {stores.length} shown
        </Typography>
        <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Button
            disabled={bulkActionsDisabled || selectedVisibleStoreCount === visibleStoreIds.length}
            onClick={() => onBulkStoreAssignmentChange(visibleStoreIds, true)}
            size="small"
            variant="text"
            sx={{
              color: adminColors.primary,
              fontSize: adminTypography.helper.fontSize,
              fontWeight: 600,
              minWidth: 0,
              p: 0,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Select shown
          </Button>
          <Button
            disabled={bulkActionsDisabled || selectedVisibleStoreCount === 0}
            onClick={() => onBulkStoreAssignmentChange(visibleStoreIds, false)}
            size="small"
            variant="text"
            sx={{
              color: adminColors.primary,
              fontSize: adminTypography.helper.fontSize,
              fontWeight: 600,
              minWidth: 0,
              p: 0,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            Clear shown
          </Button>
        </Stack>
      </Stack>

      {storeIdsError ? (
        <Typography sx={{ ...adminTypography.helper, color: adminColors.danger }}>
          {storeIdsError}
        </Typography>
      ) : null}

      <Box
        sx={FEATURE_FLAG_SELECTOR_LIST_SX}
      >
        {storesLoading ? (
          <Box sx={FEATURE_FLAG_SELECTOR_STATE_SX}>
            <CircularProgress size={24} />
          </Box>
        ) : storesError ? (
          <Box sx={FEATURE_FLAG_SELECTOR_STATE_SX}>
            <Typography sx={{ ...adminTypography.helper, color: adminColors.danger }}>
              {storesError}
            </Typography>
          </Box>
        ) : stores.length === 0 ? (
          <Box sx={FEATURE_FLAG_SELECTOR_STATE_SX}>
            <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
              No stores found.
            </Typography>
          </Box>
        ) : stores.map((store) => (
          <Stack
            key={store.id}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              borderBottom: `1px solid ${adminColors.divider}`,
              px: 1,
              py: 0.75,
              '&:last-of-type': { borderBottom: 0 },
            }}
          >
            <Checkbox
              checked={selectedStoreIdSet.has(store.id)}
              onChange={(event) => onStoreAssignmentChange(store.id, event.target.checked)}
              size="small"
              sx={{ color: adminColors.primary, p: 0.25 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                {store.name}
              </Typography>
              {store.address ? (
                <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                  {store.address}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        ))}
      </Box>
    </Stack>
  );
};

export default React.memo(FeatureFlagStoreSelector);
